import React, { useMemo } from 'react';
import { useI18n } from '../../context/I18nContext';
import { useTranslations } from '../../hooks/useTranslations';
import { useSlidingWindow } from '../../hooks/useSlidingWindow';
import { Project, Lead, Payment } from '../../types';
import Card, { CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Calendar, FolderOpen, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { navigateToProject } from '../../utils/navigationUtils';
import { isDemoMode } from '../../services/dataScope';
import Button from '../ui/Button';
import SlidingWindowNav from './SlidingWindowNav';
import {
  DASHBOARD_WINDOW_LIST,
  DASHBOARD_WINDOW_ROW,
  DASHBOARD_WINDOW_SIZE,
} from './dashboardWindowLayout';

interface RecentProjectsProps {
  projects: Project[];
  leads: Lead[];
  payments: Payment[];
  showFirstProjectCta?: boolean;
  onCreateFirstProject?: () => void;
}

const RecentProjects: React.FC<RecentProjectsProps> = ({
  projects,
  leads,
  showFirstProjectCta = false,
  onCreateFirstProject,
}) => {
  const { t } = useI18n();
  const { formatCurrency, formatDateShort } = useTranslations();
  const navigate = useNavigate();
  const interactive = !isDemoMode();

  const sortedProjects = useMemo(
    () =>
      [...projects].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      ),
    [projects]
  );

  const { visible, canPrev, canNext, prev, next, showNav } = useSlidingWindow(
    sortedProjects,
    DASHBOARD_WINDOW_SIZE
  );

  const getStatusBadge = (project: Project) => {
    const getBadgeConfig = (status: Project['status']): { classes: string; label: string } => {
      const configMap: Record<Project['status'], { classes: string; label: string }> = {
        BRIEF_RECEIVED: {
          classes: 'bg-blue-100/80 text-blue-700',
          label: t('projects.statusBriefReceived'),
        },
        IN_PRODUCTION: {
          classes: 'bg-violet-100/80 text-violet-700',
          label: t('projects.statusInProduction'),
        },
        V1_SENT: {
          classes: 'bg-amber-100/80 text-amber-700',
          label: t('projects.statusFirstVersion'),
        },
        CHANGES_REQUESTED: {
          classes: 'bg-orange-100/80 text-orange-700',
          label: t('projects.statusCorrections'),
        },
        DELIVERED: {
          classes: 'bg-emerald-100/80 text-emerald-700',
          label: t('projects.statusDelivered'),
        },
        ARCHIVED: {
          classes: 'bg-slate-100/80 text-slate-700',
          label: t('projects.statusArchived'),
        },
      };
      return (
        configMap[status] || {
          classes: 'bg-gray-100/80 text-gray-700',
          label: t('projects.statusInProduction'),
        }
      );
    };

    const config = getBadgeConfig(project.status);

    return (
      <span className={`inline-flex items-center font-medium rounded-full px-2 py-1 text-xs whitespace-nowrap ${config.classes}`}>
        {config.label}
      </span>
    );
  };

  const handleViewProject = (project: Project) => {
    navigateToProject(navigate, project.id);
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between gap-2 min-h-7">
          <CardTitle className="flex items-center text-base min-w-0">
            <FolderOpen className="w-5 h-5 text-blue-500 mr-2 shrink-0" />
            <span className="truncate">{t('dashboard.recentProjects')}</span>
          </CardTitle>
          {showNav && (
            <SlidingWindowNav
              canPrev={canPrev}
              canNext={canNext}
              onPrev={prev}
              onNext={next}
              tone="blue"
            />
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {sortedProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-8 px-4 text-center">
            {showFirstProjectCta && onCreateFirstProject ? (
              <>
                <p className="text-sm text-gray-600 leading-snug">{t('dashboard.firstProjectHint')}</p>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  icon={<Plus className="w-4 h-4" />}
                  onClick={onCreateFirstProject}
                >
                  {t('dashboard.firstProjectCta')}
                </Button>
              </>
            ) : (
              <p className="text-gray-500">{t('dashboard.noRecentProjects')}</p>
            )}
          </div>
        ) : (
          <div className={DASHBOARD_WINDOW_LIST}>
            {visible.map((project) => {
              const lead = leads.find((l) => l.id === project.leadId);

              return (
                <div
                  key={project.id}
                  className={`flex items-center justify-between gap-2 px-4 ${DASHBOARD_WINDOW_ROW} overflow-hidden bg-blue-50/80 rounded-xl border border-blue-200/60 transition-colors duration-150 ${
                    interactive
                      ? 'cursor-pointer hover:bg-blue-50 hover:border-blue-300'
                      : ''
                  }`}
                  onClick={interactive ? () => handleViewProject(project) : undefined}
                  role={interactive ? 'button' : undefined}
                  tabIndex={interactive ? 0 : undefined}
                  onKeyDown={
                    interactive
                      ? (e) => {
                          if (e.key === 'Enter') handleViewProject(project);
                        }
                      : undefined
                  }
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">
                      {project.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-600 min-w-0 overflow-hidden">
                      <p className="truncate shrink min-w-0">
                        {lead?.name || '-'}
                      </p>
                      <span className="text-gray-400 shrink-0">•</span>
                      <span className="shrink-0 tabular-nums">
                        {formatCurrency(project.amount || 0)}
                      </span>
                      <span className="text-gray-400 shrink-0">•</span>
                      <div className="flex items-center gap-1 shrink-0">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDateShort(project.deadline || project.updatedAt)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center ml-2 shrink-0">
                    {getStatusBadge(project)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentProjects;
